import React, { useState, useEffect } from 'react';
import { TextField } from '@fluentui/react/lib/TextField';
import { PrimaryButton, DefaultButton } from '@fluentui/react';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { MessageBar, MessageBarType } from '@fluentui/react';
import { Panel, PanelType } from '@fluentui/react/lib/Panel';
import { useBoolean } from '@fluentui/react-hooks';
import { ApiHelper } from '../ApiHelper';
import { formatDistanceToNow } from 'date-fns';
import AzDevOpsIcon from '../assets/images/Azure-DevOps.svg';
import { COMMIT } from '../utils/constants';
import { VsoLink, ParentButtonsComponent } from './CommonElements.tsx';
import { CommitDetailsGraph } from './CommitDetailsGraph';
import { useConfig } from '../configContext';
import '../styles/StackTrace.css';
import type { CommitDetails } from '../interfaces/CommitDetails.ts';

interface CommitMetaData {
    commitId: string;
    prId: string;
    authorName: string;
    authorEmail: string;
    prMergeDate: string;
    title: string;
    projectName: string;
    repositoryName: string;
    deploymentStatusAsString?: string;
}

interface Frame {
    originalFrame: string;
    matchingCommits: CommitMetaData[];
}

const StackTracePage = () => {
    const [stackTrace, setStackTrace] = useState('');
    const [lookbackDays, setLookbackDays] = useState('30');
    const [frames, setFrames] = useState<Frame[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedFrames, setExpandedFrames] = useState<Set<number>>(new Set());
    const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
    const [selectedCommit, setSelectedCommit] = useState<CommitDetails | null>(null);
    const [selectedMetric, setSelectedMetric] = useState<number>(2);
    const [loadingCommit, setLoadingCommit] = useState(false);
    const config = useConfig();

    const selectedServiceName = localStorage.getItem('selectedService');
    const service = config?.services?.find(s => s.name === selectedServiceName);

    useEffect(() => {
        window.document.title = 'PR Guardian - Stack Trace Analyzer';
    }, []);

    const handleSubmit = async () => {
        if (!stackTrace.trim()) {
            setError('Please enter a stack trace');
            return;
        }

        if (!selectedServiceName) {
            setError('Please select a service first');
            return;
        }

        setLoading(true);
        setError(null);
        setFrames([]);

        try {
            const result = await ApiHelper<Frame[]>(
                `/api/service/${selectedServiceName}/stacktrace`,
                'POST',
                {
                    stackTrace: stackTrace,
                    maxLookbackDays: parseInt(lookbackDays) || 30
                }
            );

            if (result && Array.isArray(result)) {
                setFrames(result);

                // Auto-expand frames with fewer than 3 commits
                const autoExpandSet = new Set<number>();
                result.forEach((frame, index) => {
                    if (frame.matchingCommits && frame.matchingCommits.length > 0 && frame.matchingCommits.length < 3) {
                        autoExpandSet.add(index);
                    }
                });
                setExpandedFrames(autoExpandSet);

                if (result.length === 0) {
                    setError('No stack frames found in the provided trace');
                }
            } else {
                setError('Invalid response from server');
            }
        } catch (err) {
            setError('Failed to analyze stack trace. Please try again.');
            console.error('Error analyzing stack trace:', err);
        } finally {
            setLoading(false);
        }
    };

    const toggleFrame = (index: number) => {
        const newExpanded = new Set(expandedFrames);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedFrames(newExpanded);
    };

    const getLatestCommitTime = (commits: CommitMetaData[]) => {
        if (!commits || commits.length === 0) return null;

        const sortedCommits = [...commits].sort((a, b) =>
            new Date(b.prMergeDate).getTime() - new Date(a.prMergeDate).getTime()
        );

        return sortedCommits[0].prMergeDate;
    };

    const openCommitDetails = async (commit: CommitMetaData, metricType: number = 2) => {
        setLoadingCommit(true);
        setSelectedMetric(metricType);
        openPanel();

        try {
            // Fetch full commit details with metrics data
            const commitDetails = await ApiHelper<CommitDetails>(
                `/api/service/${selectedServiceName}/commits/${commit.commitId}/details`
            );

            setSelectedCommit(commitDetails);
        } catch (err) {
            console.error('Error loading commit details:', err);
            setError('Failed to load commit details. Please try again.');
            dismissPanel();
        } finally {
            setLoadingCommit(false);
        }
    };

    const getAzDoUrl = (commit: CommitMetaData) => {
        if (!service) return '#';
        return `https://${service.azDOOrg}.visualstudio.com/${commit.projectName}/_git/${commit.repositoryName}/commit/${commit.commitId.split('-').pop()}`;
    };

    const extractMethodSignature = (frameText: string): string => {
        // Remove leading "at " if present
        let text = frameText.trim();
        if (text.toLowerCase().startsWith('at ')) {
            text = text.substring(3).trim();
        }

        // Extract everything before the first '(' (removes parameters)
        const parenIndex = text.indexOf('(');
        if (parenIndex !== -1) {
            text = text.substring(0, parenIndex);
        }

        return text.trim();
    };

    return (
        <div className="page-container">
            <div className="stack-trace-container">
                <h2 className="stack-trace-header">Stack Trace Analyzer</h2>
                <p className="stack-trace-description">
                    Paste a stack trace below to find which commits modified the code in each frame.
                </p>

                <div className="stack-trace-input-section">
                    <TextField
                        label="Stack Trace"
                        multiline
                        rows={10}
                        value={stackTrace}
                        onChange={(e, newValue) => setStackTrace(newValue || '')}
                        placeholder="Paste your stack trace here...&#10;Example:&#10;at MyNamespace.MyClass.MyMethod() in C:\Path\File.cs:line 42&#10;at AnotherNamespace.AnotherClass.AnotherMethod()"
                        disabled={loading}
                    />

                    <TextField
                        label="Lookback Period (days)"
                        type="number"
                        value={lookbackDays}
                        onChange={(e, newValue) => setLookbackDays(newValue || '30')}
                        description="How many days back to search for commits (default: 30)"
                        disabled={loading}
                        styles={{ root: { maxWidth: 200, marginTop: 10 } }}
                    />

                    <div className="stack-trace-buttons">
                        <PrimaryButton
                            text="Analyze"
                            onClick={handleSubmit}
                            disabled={loading || !selectedServiceName}
                        />
                        <DefaultButton
                            text="Clear"
                            onClick={() => {
                                setStackTrace('');
                                setFrames([]);
                                setError(null);
                                setExpandedFrames(new Set());
                            }}
                            disabled={loading}
                        />
                    </div>
                </div>

                {!selectedServiceName && (
                    <MessageBar messageBarType={MessageBarType.warning}>
                        Please select a service from the dropdown in the navigation bar before analyzing a stack trace.
                    </MessageBar>
                )}

                {error && (
                    <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
                        {error}
                    </MessageBar>
                )}

                {loading && (
                    <div className="stack-trace-loading">
                        <Spinner size={SpinnerSize.large} label="Analyzing stack trace..." />
                    </div>
                )}

                {!loading && frames.length > 0 && (
                    <div className="stack-trace-results">
                        <h3>Results ({frames.length} frames)</h3>
                        {frames.map((frame, index) => {
                            const isExpanded = expandedFrames.has(index);
                            const latestCommitDate = getLatestCommitTime(frame.matchingCommits);
                            const hasCommits = frame.matchingCommits && frame.matchingCommits.length > 0;
                            const methodSignature = extractMethodSignature(frame.originalFrame);

                            return (
                                <div key={index} className="stack-trace-frame">
                                    <div
                                        className="stack-trace-frame-header"
                                        onClick={() => toggleFrame(index)}
                                    >
                                        <span className="stack-trace-frame-icon">
                                            {isExpanded ? '−' : '+'}
                                        </span>
                                        <div className="stack-trace-frame-info-compact">
                                            <code className="stack-trace-frame-text">
                                                {methodSignature}
                                            </code>
                                            <span className="stack-trace-frame-separator">•</span>
                                            {hasCommits ? (
                                                <>
                                                    <span className="stack-trace-commit-count">
                                                        {frame.matchingCommits.length} commit{frame.matchingCommits.length !== 1 ? 's' : ''}
                                                    </span>
                                                    {latestCommitDate && (
                                                        <>
                                                            <span className="stack-trace-frame-separator">•</span>
                                                            <span className="stack-trace-latest-commit">
                                                                {formatDistanceToNow(new Date(latestCommitDate), { addSuffix: true })}
                                                            </span>
                                                        </>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="stack-trace-no-commits">No matching commits</span>
                                            )}
                                        </div>
                                    </div>

                                    {isExpanded && hasCommits && (
                                        <div className="stack-trace-commits-list">
                                            {frame.matchingCommits.map((commit, commitIndex) => (
                                                <div key={commitIndex} className="stack-trace-commit-item">
                                                    <span className="stack-trace-commit-title">
                                                        {commit.title}
                                                    </span>
                                                    <span className="stack-trace-commit-separator">•</span>
                                                    <span className="stack-trace-commit-author">
                                                        {commit.authorName}
                                                    </span>
                                                    <span className="stack-trace-commit-separator">•</span>
                                                    <span className="stack-trace-commit-deployment">
                                                        {commit.deploymentStatusAsString || 'Not deployed'}
                                                    </span>
                                                    <span className="stack-trace-commit-separator">•</span>
                                                    <span className="stack-trace-commit-date">
                                                        {formatDistanceToNow(new Date(commit.prMergeDate), { addSuffix: true })}
                                                    </span>
                                                    <span className="stack-trace-commit-separator">•</span>
                                                    <button
                                                        className="stack-trace-action-button-inline"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openCommitDetails(commit, 2);
                                                        }}
                                                        title="View commit details with Exceptions metric"
                                                    >
                                                        Details
                                                    </button>
                                                    <a
                                                        href={getAzDoUrl(commit)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="stack-trace-ado-link-inline"
                                                        title="Open in Azure DevOps"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <img src={AzDevOpsIcon} width="16" height="16" alt="ADO" />
                                                        ADO
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {service && (
                <Panel
                    isLightDismiss
                    isOpen={isOpen}
                    type={PanelType.medium}
                    onDismiss={dismissPanel}
                    closeButtonAriaLabel="Close"
                    headerText={selectedCommit?.commitMetaData?.title || 'Loading...'}
                >
                    {loadingCommit && (
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                            <Spinner size={SpinnerSize.large} label="Loading commit details..." />
                        </div>
                    )}
                    {!loadingCommit && selectedCommit && (
                        <>
                            <div style={{ marginLeft: '10px' }}>
                                <p>Author: {selectedCommit.commitMetaData.authorName}</p>
                                <p>Deployment: {selectedCommit.commitMetaData.deploymentStatusAsString || 'Unknown'}</p>
                                <p>
                                    <VsoLink commitMetadata={selectedCommit.commitMetaData} service={service} />
                                </p>
                                <p>
                                    <a href={`/service/${service.name}/CommitDetails/${selectedCommit.commitId}?metric=${selectedMetric}`} target="_blank" rel="noopener noreferrer">Full details page</a>
                                </p>
                            </div>
                            <ParentButtonsComponent
                                serviceId={service.id}
                                commitDetails={selectedCommit}
                                env={service.defaultConfig?.environmentId || 0}
                                processId={service.defaultConfig?.processId || 0}
                                metric={selectedMetric}
                            />
                            <CommitDetailsGraph
                                serviceId={service.id}
                                env={service.defaultConfig?.environmentId || 0}
                                process={service.defaultConfig?.processId || 0}
                                type={COMMIT}
                                commitDetails={selectedCommit}
                                metric={selectedMetric}
                                showBottomButtons={false}
                            />
                        </>
                    )}
                </Panel>
            )}
        </div>
    );
};

export default StackTracePage;
